/**
 * Centralized style constants for the Welcome page
 * Reduces duplication and ensures consistency across components
 */

// Typography Styles
export const TYPOGRAPHY_STYLES = {
  // Header styles
  sectionHeader: {
    fontSize: 20,
    fontWeight: 500,
    color: "#009E77"
  } as const,

  cardHeader: {
    fontSize: 20,
    fontWeight: 400,
    color: "#009E77"
  } as const,

  // Body text styles
  body: {
    fontSize: 14,
    color: "#666",
    lineHeight: 1.4
  } as const,

  bodyBold: {
    fontSize: 14,
    color: "#333",
    fontWeight: 500
  } as const,

  // Decorative middot between configuration card sub-links
  subLinkSeparator: {
    fontSize: 14,
    color: "#999",
    lineHeight: "20px",
    padding: "4px 0",
    userSelect: "none"
  } as const,

  // Card description with text clipping
  cardDescription: {
    fontSize: 14,
    color: "#666",
    lineHeight: 1.4,
    overflow: "hidden",
    display: "-webkit-box",
    WebkitLineClamp: 3,
    WebkitBoxOrient: "vertical",
    marginBottom: 0
  } as const
};

// Application Card Styles
export const CARD_STYLES = {
  width: { xs: "100%", sm: 280 },
  minWidth: 254,
  maxWidth: "100%",
  minHeight: 164,
  padding: "12px 16px",
  backgroundColor: "#fff",
  borderRadius: "3px",
  border: "1px solid #999",
  boxSizing: "border-box",
  cursor: "pointer",
  boxShadow: "rgba(0, 0, 0, .3) 0 0 4px 1px",
  transition: "box-shadow 0.2s",
  "&:hover": {
    boxShadow: "0 4px 8px rgba(0,0,0,0.6)"
  },
  display: "flex",
  flexDirection: "column"
} as const;

// Link Styles
export const LINK_STYLES = {
  // Standard link with hover underline
  standard: {
    fontSize: 14,
    color: "#009E77",
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline"
    }
  } as const,

  // Configuration card sub-link — contrast-safe colour (7.31:1) + ≥24px vertical hit area
  cardSubLink: {
    fontSize: 14,
    color: "#1B5A8A",
    textDecoration: "none",
    display: "inline-block",
    // keeps a multi-word label intact so wrapping happens between links
    whiteSpace: "nowrap",
    padding: "4px 0",
    lineHeight: "20px",
    // must precede :hover — same specificity, so source order decides
    "&:visited": {
      color: "#0F3D5C"
    },
    "&:hover": {
      textDecoration: "underline"
    },
    "&:focus-visible": {
      outline: "2px solid #1B5A8A",
      outlineOffset: "2px"
    }
  } as const,

  // Card wrapper link (no decoration)
  cardWrapper: {
    textDecoration: "none",
    display: "block",
    color: "inherit",
    "& .application-icon svg": { color: "white" },
    boxShadow: "none",
    outline: "none",
    "&:hover": {
      color: "inherit",
      textDecoration: "none",
      boxShadow: "none",
      outline: "none"
    },
    "&:focus": {
      boxShadow: "none",
      outline: "none"
    },
    "&:active": {
      color: "inherit"
    },
    "&:visited": {
      color: "inherit"
    }
  } as const
};

// Container/Box Styles
export const CONTAINER_STYLES = {
  // Flex row with centered alignment
  flexRowCenter: {
    display: "flex",
    alignItems: "center"
  } as const,

  // Flex row with centered alignment and gap
  flexRowCenterGap: {
    display: "flex",
    alignItems: "center",
    gap: "6px"
  } as const,

  // Inline wrapping list of sub-links joined by a decorative separator
  subLinkInlineList: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    columnGap: "6px",
    rowGap: 0
  } as const,

  // Flex column layout
  flexColumn: {
    display: "flex",
    flexDirection: "column"
  } as const,

  // Flex column with gap
  flexColumnGap: {
    display: "flex",
    flexDirection: "column",
    gap: "8px"
  } as const,

  // Flex wrap for card grids
  flexWrap: {
    display: "flex",
    flexWrap: "wrap",
    gap: "16px"
  } as const,

  // Support contacts details container
  supportDetails: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    marginLeft: "48px"
  } as const
};

// Card Icon Box Styles
export const ICON_BOX_STYLES = {
  base: {
    color: "#FFFFFF",
    padding: "4px",
    height: 24,
    width: 24,
    borderRadius: "3px",
    marginRight: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxSizing: "content-box"
  } as const
};
